import {
  type Announcements,
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type Modifier,
  PointerSensor,
  type PointerSensorOptions,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  type ContactDto,
  checkStepNote,
  type EventDetail,
  JOIN_PATH,
  MAX_CONTACT_NAME,
  MAX_STEP_TITLE,
  type MeResponse,
  type StepDto,
  templates,
  worstLink,
} from '@eventping/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Bell,
  BellOff,
  Check,
  Circle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Link2,
  Link2Off,
  MessageCircle,
  Pencil,
  RefreshCw,
  RotateCcw,
  Send,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react'
import { type FormEvent, type KeyboardEvent, type PointerEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { CreditBanner } from '@/components/credit'
import {
  Card,
  ChannelsNotice,
  FieldError,
  Footer,
  Page,
  SectionTitle,
  timeOf,
} from '@/components/shell'
import { TemplateChips } from '@/components/templates'
import { Thread } from '@/components/thread'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ApiFailure, api, useLive, useMe } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CreatorHeader } from './home'

type Tab = 'steps' | 'people' | 'chat'

export function EventPage({ eventId }: { eventId: string }) {
  const me = useMe()
  const key = ['event', eventId] as const
  const detail = useQuery({
    queryKey: key,
    queryFn: () => api<EventDetail>(`/api/events/${eventId}`),
    enabled: Boolean(me.data?.user),
  })
  useLive(detail.data ? `/api/events/${eventId}/stream` : null, key)
  const [tab, setTab] = useState<Tab>('steps')
  // Every refresh of the event (a step's texts going out, a sign-up confirmed) can change the
  // balance: refetch the badge with it.
  const client = useQueryClient()
  const updatedAt = detail.dataUpdatedAt
  useEffect(() => {
    if (updatedAt) void client.invalidateQueries({ queryKey: ['credit'] })
  }, [updatedAt, client])

  if (me.data && !me.data.user) {
    return (
      <Page>
        <p>
          Please{' '}
          <Link to="/" className="underline">
            sign in
          </Link>{' '}
          first.
        </p>
      </Page>
    )
  }
  if (!me.data || detail.isPending) return <Page>{null}</Page>
  if (detail.isError) {
    return (
      <Page header={<CreatorHeader me={me.data} />}>
        <p>{detail.error.message}</p>
      </Page>
    )
  }
  const d = detail.data
  const archived = d.event.status === 'archived'
  const joined = d.contacts.filter((c) => c.status === 'active').length
  const unread = d.messages.filter((m) => m.kind === 'text').length
  const pendingCount = d.contacts.filter((c) => c.status === 'pending').length
  const texts = me.data.textsEnabled

  return (
    <Page header={<CreatorHeader me={me.data} />}>
      <ChannelsNotice channels={me.data.channels} textsEnabled={me.data.textsEnabled} />
      {texts && <CreditBanner />}
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="inline-flex min-h-10 items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> All events
        </Link>
        {/* The follower page as followers see it right now (routes/preview.tsx). */}
        <Button asChild variant="outline" size="sm">
          <Link to="/events/$eventId/preview" params={{ eventId }}>
            <Eye /> Preview as follower
          </Link>
        </Button>
      </div>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-balance break-words">{d.event.title}</h1>
        <p className="text-sm text-muted-foreground">
          {texts ? 'Texts come from' : 'Updates from'} {d.event.senderName} · Event code #
          {d.event.code}
          {archived && ' · Archived'}
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Event sections"
        className="mb-5 grid grid-cols-3 gap-1 rounded-2xl bg-muted p-1"
      >
        {(
          [
            {
              id: 'steps',
              label: 'Steps',
              icon: Check,
              count: `${d.steps.filter((s) => s.status === 'done').length}/${d.steps.length}`,
            },
            {
              id: 'people',
              label: 'People',
              icon: Users,
              count: String(joined),
              badge: pendingCount,
            },
            { id: 'chat', label: 'Chat', icon: MessageCircle, count: unread ? String(unread) : '' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex min-h-12 items-center justify-center gap-1.5 rounded-xl text-base font-medium',
              tab === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
            )}
          >
            <t.icon className="size-4.5" aria-hidden />
            {t.label}
            {t.count && (
              <span className="text-sm font-normal text-muted-foreground">{t.count}</span>
            )}
            {'badge' in t && t.badge > 0 && (
              <span className="grid min-w-5 place-items-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                {t.badge}
                <span className="sr-only"> waiting for approval</span>
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'steps' && <StepsPanel d={d} me={me.data} />}
      {tab === 'people' && <PeoplePanel d={d} me={me.data} />}
      {tab === 'chat' && (
        <Card>
          <SectionTitle>Conversation</SectionTitle>
          <p className="-mt-2 mb-4 text-sm text-muted-foreground">
            {texts
              ? 'Replies to your texts show up here, along with messages from the event page. Messages here are not texted out.'
              : 'Messages from the event page show up here. Everyone following sees them.'}
          </p>
          <Thread
            messages={d.messages}
            viewer="creator"
            disabled={archived}
            onSend={async (body) => {
              await api(`/api/events/${d.event.id}/messages`, { body: { body } })
              // Shown at once, whether or not the live stream gets through.
              await detail.refetch()
            }}
          />
        </Card>
      )}
      <Footer />
    </Page>
  )
}

function useRefresh(eventId: number) {
  const client = useQueryClient()
  return async () => {
    await client.invalidateQueries({ queryKey: ['event', String(eventId)] })
    // A send may have used credit: the header badge follows.
    await client.invalidateQueries({ queryKey: ['credit'] })
  }
}

async function attempt(fn: () => Promise<unknown>, refresh: () => Promise<void>) {
  try {
    await fn()
  } catch (e) {
    toast.error((e as Error).message)
  } finally {
    await refresh()
  }
}

// ------------------------------------------------------------------ steps

function recipients(d: EventDetail, step: StepDto, texts: boolean): number {
  return d.contacts.filter(
    (c) => c.status === 'active' && step.notify && ((texts && c.sms && !c.optedOut) || c.email),
  ).length
}

/**
 * The pointer sensor for mouse and pen only: touches go to the TouchSensor and its short
 * press delay, so a finger that lands on a handle and moves right away does not start a drag.
 */
class MouseOrPenSensor extends PointerSensor {
  static override activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (
        { nativeEvent: event }: PointerEvent,
        { onActivation }: PointerSensorOptions,
      ): boolean => {
        if (!event.isPrimary || event.button !== 0 || event.pointerType === 'touch') return false
        onActivation?.({ event })
        return true
      },
    },
  ]
}

/** Steps only move up and down: no sideways drift, so no sideways scroll on a narrow phone. */
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 })

function StepsPanel({ d, me }: { d: EventDetail; me: MeResponse }) {
  const [editing, setEditing] = useState(false)
  const [trigger, setTrigger] = useState<StepDto | null>(null)
  const [applying, setApplying] = useState(false)
  const refresh = useRefresh(d.event.id)
  const client = useQueryClient()
  const archived = d.event.status === 'archived'
  const base = `/api/events/${d.event.id}`
  const key = ['event', String(d.event.id)]
  const sensors = useSensors(
    useSensor(MouseOrPenSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const sortable = !archived && d.steps.length > 1
  // Each Notify step texts everyone, so an event has only so many; Quiet steps are free.
  const notifyCount = d.steps.filter((s) => s.notify).length
  const notifyMax = me.limits.notifyStepsPerEvent
  const notifyFull = notifyCount >= notifyMax
  const titleOf = (id: string | number) =>
    d.steps.find((s) => s.id === Number(id))?.title ?? 'The step'
  const placeOf = (id: string | number | undefined) =>
    d.steps.findIndex((s) => s.id === Number(id)) + 1
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      `Picked up ${titleOf(active.id)}, step ${placeOf(active.id)} of ${d.steps.length}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} moved to step ${placeOf(over.id)} of ${d.steps.length}.`
        : `${titleOf(active.id)} is no longer over the list.`,
    onDragEnd: ({ active, over }) =>
      over
        ? `${titleOf(active.id)} dropped at step ${placeOf(over.id)} of ${d.steps.length}.`
        : `${titleOf(active.id)} dropped. The order did not change.`,
    onDragCancel: ({ active }) =>
      `Moving ${titleOf(active.id)} was canceled. The order did not change.`,
  }

  async function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const from = d.steps.findIndex((s) => s.id === active.id)
    const to = d.steps.findIndex((s) => s.id === over.id)
    if (from < 0 || to < 0) return
    const steps = arrayMove(d.steps, from, to)
    // Shown at once; put back if the server says no.
    await client.cancelQueries({ queryKey: key })
    const before = client.getQueryData<EventDetail>(key)
    client.setQueryData<EventDetail>(key, (old) => (old ? { ...old, steps } : old))
    try {
      await api(`${base}/steps/order`, { method: 'PUT', body: { ids: steps.map((s) => s.id) } })
    } catch (e) {
      if (before) client.setQueryData(key, before)
      toast.error(`Could not save the new order. ${(e as Error).message}`)
    } finally {
      await refresh()
    }
  }

  async function applyTemplate(
    steps: readonly { title: string; notify: boolean; hidden?: boolean }[],
  ) {
    setApplying(true)
    await attempt(() => api(`${base}/steps/bulk`, { body: { steps } }), refresh)
    setApplying(false)
  }

  return (
    <div className="space-y-4">
      <Card>
        <SectionTitle
          aside={
            !archived &&
            d.steps.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setEditing((v) => !v)}>
                <Pencil /> {editing ? 'Done editing' : 'Edit steps'}
              </Button>
            )
          }
        >
          Timeline
        </SectionTitle>
        {d.steps.length === 0 && (
          <div className="space-y-4">
            <p className="rounded-xl bg-muted px-4 py-3 text-muted-foreground">
              Add the steps you expect, from first to last, or start from a template. You can
              reorder them later.
            </p>
            {!archived && (
              <TemplateChips disabled={applying} onPick={(t) => t && void applyTemplate(t.steps)} />
            )}
          </div>
        )}
        {d.steps.length > 0 && (
          <p className="mb-3 text-sm text-muted-foreground" data-testid="notify-count">
            <Bell className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
            Notify steps: {notifyCount} of {notifyMax}
            {notifyFull && !archived && '. That’s the most for one event: new steps are Quiet.'}
          </p>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[verticalOnly]}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable:
                'To move a step, press space or enter on its handle, use the up and down arrow keys, then press space or enter again to drop it, or escape to cancel.',
            },
          }}
          onDragEnd={(e) => void onDragEnd(e)}
        >
          <SortableContext items={d.steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ol className="space-y-2.5">
              {d.steps.map((step) => (
                <SortableStep key={step.id} id={step.id} enabled={sortable}>
                  {(handle) => (
                    <StepRow
                      step={step}
                      handle={handle}
                      editing={editing && !archived}
                      archived={archived}
                      onTrigger={() => setTrigger(step)}
                      base={base}
                      refresh={refresh}
                      notifyFull={notifyFull}
                      notifyMax={notifyMax}
                      texts={me.textsEnabled}
                    />
                  )}
                </SortableStep>
              ))}
            </ol>
          </SortableContext>
        </DndContext>
        {!archived && (
          <AddStep
            base={base}
            refresh={refresh}
            count={d.steps.length}
            max={me.limits.stepsPerEvent}
            notifyFull={notifyFull}
            notifyMax={notifyMax}
            texts={me.textsEnabled}
          />
        )}
      </Card>
      <TriggerDialog d={d} me={me} step={trigger} onClose={() => setTrigger(null)} />
      <p className="text-center text-sm text-muted-foreground">
        <Bell className="inline size-4" aria-hidden /> Notify steps reach everyone who signed up.{' '}
        <BellOff className="inline size-4" aria-hidden /> Quiet steps only update the page.
        <br />
        <Eye className="inline size-4" aria-hidden /> Public steps show on followers’ timelines from
        the start. <EyeOff className="inline size-4" aria-hidden /> Hidden steps show once they’re
        sent.
      </p>
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void attempt(
              () =>
                api(base, { method: 'PATCH', body: { status: archived ? 'active' : 'archived' } }),
              refresh,
            )
          }
        >
          {archived ? <ArchiveRestore /> : <Archive />}
          {archived ? 'Restore event' : 'Archive event'}
        </Button>
      </div>
    </div>
  )
}

type Handle = React.ReactNode

/**
 * One sortable step: the <li> moves, and only the grip handle starts a drag (mouse, a short
 * press on touch, or the keyboard), so the rest of the row scrolls the page as usual.
 */
function SortableStep({
  id,
  enabled,
  children,
}: {
  id: number
  enabled: boolean
  children: (handle: Handle) => React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !enabled })
  const handle = enabled ? (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label="Drag to reorder"
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        '-my-1 -ml-1.5 grid h-10 w-8 shrink-0 cursor-grab touch-none place-items-center rounded-lg text-muted-foreground outline-none select-none [-webkit-touch-callout:none] hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
        isDragging && 'cursor-grabbing text-foreground',
      )}
    >
      <GripVertical className="size-5" aria-hidden />
    </button>
  ) : null
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('relative', isDragging && 'z-10 rounded-2xl bg-card shadow-lg')}
    >
      {children(handle)}
    </li>
  )
}

function StepRow({
  step,
  handle,
  editing,
  archived,
  onTrigger,
  base,
  refresh,
  notifyFull,
  notifyMax,
  texts,
}: {
  step: StepDto
  handle: Handle
  editing: boolean
  archived: boolean
  onTrigger: () => void
  base: string
  refresh: () => Promise<void>
  /** The event has its maximum of Notify steps: a Quiet one cannot be switched to Notify. */
  notifyFull: boolean
  notifyMax: number
  /** Texts-off mode hides the "Texted" count. */
  texts: boolean
}) {
  const url = `${base}/steps/${step.id}`
  const done = step.status === 'done'
  const sending = step.status === 'sending'
  const notifyLocked = !step.notify && notifyFull
  const lockedHint = `step-${step.id}-notify-full`

  return (
    <div
      className={cn(
        'rounded-2xl border p-3.5',
        done && 'border-transparent bg-done-soft',
        sending && 'border-waiting bg-waiting-soft',
      )}
      data-testid={`step-${step.title}`}
      data-status={step.status}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {handle}
          <span
            className={cn(
              'mt-0.5 grid size-8 shrink-0 place-items-center rounded-full',
              done
                ? 'bg-done text-white dark:text-background'
                : sending
                  ? 'bg-waiting text-white'
                  : 'border-2 text-muted-foreground',
            )}
            aria-hidden
          >
            {done ? (
              <Check className="size-4.5" />
            ) : sending ? (
              <Clock className="size-4.5" />
            ) : (
              <Circle className="size-2.5 fill-current" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            {editing ? (
              <StepTitleField step={step} url={url} refresh={refresh} disabled={sending} />
            ) : (
              <p className="font-medium break-words">{step.title}</p>
            )}
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                {step.notify ? <Bell className="size-3.5" /> : <BellOff className="size-3.5" />}
                {step.notify ? 'Notify' : 'Quiet'}
              </span>
              {/* Once sent every step is on followers' timelines, so only unsent ones say. */}
              {!done && (
                <span className="inline-flex items-center gap-1" data-testid="visibility">
                  {step.hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  {step.hidden ? 'Hidden until sent' : 'Public'}
                </span>
              )}
              {done && step.doneAt && <span>Done {timeOf(step.doneAt)}</span>}
              {done && texts && <span>Texted {step.textedCount}</span>}
            </p>
            {step.note && <p className="mt-1 text-sm">“{step.note}”</p>}
          </div>
        </div>
        {!editing && !archived && step.status === 'pending' && (
          <Button onClick={onTrigger} className="w-full shrink-0 sm:w-auto">
            Mark done
          </Button>
        )}
      </div>
      {sending && step.sendAt && <SendingBar sendAt={step.sendAt} url={url} refresh={refresh} />}
      {editing && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a Radix Switch (a button), which it labels */}
          <label className="inline-flex min-h-10 items-center gap-2 text-sm">
            <Switch
              checked={step.notify}
              disabled={sending || notifyLocked}
              onCheckedChange={(notify) =>
                void attempt(() => api(url, { method: 'PATCH', body: { notify } }), refresh)
              }
              aria-label="Notify"
              aria-describedby={notifyLocked ? lockedHint : undefined}
            />
            Notify
          </label>
          {notifyLocked && (
            <p id={lockedHint} className="basis-full text-sm text-muted-foreground sm:basis-auto">
              All {notifyMax} Notify steps are in use. Switch another step to Quiet first.
            </p>
          )}
          {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a Radix Switch (a button), which it labels */}
          <label className="inline-flex min-h-10 items-center gap-2 text-sm">
            <Switch
              checked={!step.hidden}
              disabled={sending}
              onCheckedChange={(visible) =>
                void attempt(
                  () => api(url, { method: 'PATCH', body: { hidden: !visible } }),
                  refresh,
                )
              }
              aria-label="Public: followers see it before it’s sent"
            />
            Public
          </label>
          <div className="ml-auto flex gap-2">
            {done && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void attempt(() => api(`${url}/reopen`, { method: 'POST' }), refresh)
                }
              >
                <RotateCcw /> Reopen
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Delete step"
              disabled={sending}
              onClick={() => void attempt(() => api(url, { method: 'DELETE' }), refresh)}
            >
              <Trash2 />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * A step's name in edit mode: Enter or leaving the field saves, Escape puts it back. Renaming
 * sends nothing, even on a step already sent; followers' pages show the new name.
 */
function StepTitleField({
  step,
  url,
  refresh,
  disabled,
}: {
  step: StepDto
  url: string
  refresh: () => Promise<void>
  disabled: boolean
}) {
  const [title, setTitle] = useState(step.title)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const dirty = title.trim() !== step.title
  // A rename saved here or elsewhere (another tab) shows unless this one is mid-edit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the saved name changes
  useEffect(() => {
    if (!saving) setTitle(step.title)
  }, [step.title])

  async function save() {
    if (!dirty || saving) return
    if (!title.trim()) {
      setError('Give the step a name.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await api(url, { method: 'PATCH', body: { title } })
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      void save()
    } else if (e.key === 'Escape') {
      setTitle(step.title)
      setError(null)
    }
  }

  const id = `step-${step.id}-title`
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={title}
          maxLength={MAX_STEP_TITLE}
          disabled={disabled}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => void save()}
          aria-label="Step name"
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="h-9 font-medium"
        />
        {dirty && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={saving}
            // Keep the click from blurring the field first, which would save twice.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void save()}
          >
            Save
          </Button>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function useCountdown(until: number): number {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(t)
  }, [])
  return Math.max(0, Math.ceil((until - now) / 1000))
}

function SendingBar({
  sendAt,
  url,
  refresh,
}: {
  sendAt: number
  url: string
  refresh: () => Promise<void>
}) {
  const seconds = useCountdown(sendAt)
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-2 border-t border-waiting/30 pt-3"
      role="status"
    >
      <span className="mr-auto font-medium text-waiting">
        {seconds > 0 ? `Sending in ${seconds}s` : 'Sending…'}
      </span>
      <Button
        variant="outline"
        onClick={() =>
          void attempt(async () => {
            await api(`${url}/undo`, { method: 'POST' })
            toast('Undone. Nothing was sent.')
          }, refresh)
        }
      >
        <RotateCcw /> Undo
      </Button>
      <Button
        onClick={() => void attempt(() => api(`${url}/send-now`, { method: 'POST' }), refresh)}
      >
        <Send /> Send now
      </Button>
    </div>
  )
}

function AddStep({
  base,
  refresh,
  count,
  max,
  notifyFull,
  notifyMax,
  texts,
}: {
  base: string
  refresh: () => Promise<void>
  count: number
  max: number
  notifyFull: boolean
  notifyMax: number
  /** Texts-off mode: Notify emails everyone. */
  texts: boolean
}) {
  const [title, setTitle] = useState('')
  const [wantsNotify, setNotify] = useState(true)
  const [visible, setVisible] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // At the Notify limit a new step can only be Quiet.
  const notify = wantsNotify && !notifyFull

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await api(`${base}/steps`, { body: { title, notify, hidden: !visible } })
      setTitle('')
      await refresh()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  if (count >= max)
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        This event has the maximum of {max} steps.
      </p>
    )
  return (
    <form onSubmit={submit} className="mt-4 space-y-3 rounded-2xl border border-dashed p-3.5">
      <Label htmlFor="new-step">Add a step</Label>
      <Input
        id="new-step"
        value={title}
        maxLength={MAX_STEP_TITLE}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={count === 0 ? 'Truck loaded' : 'Next step'}
        aria-invalid={Boolean(error) || undefined}
      />
      <FieldError>{error}</FieldError>
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a Radix Switch (a button), which it labels */}
          <label className="inline-flex min-h-12 items-center gap-3">
            <Switch
              checked={notify}
              disabled={notifyFull}
              onCheckedChange={setNotify}
              aria-label={
                texts ? 'Text everyone when this happens' : 'Email everyone when this happens'
              }
              aria-describedby={notifyFull ? 'new-step-notify-full' : undefined}
            />
            <span>
              {notify
                ? texts
                  ? 'Notify: text everyone'
                  : 'Notify: email everyone'
                : 'Quiet: page only'}
            </span>
          </label>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a Radix Switch (a button), which it labels */}
          <label className="inline-flex min-h-12 items-center gap-3">
            <Switch
              checked={visible}
              onCheckedChange={setVisible}
              aria-label="Public: followers see it before it’s sent"
            />
            <span>{visible ? 'Public: on the timeline now' : 'Hidden until sent'}</span>
          </label>
        </div>
        <Button type="submit" variant="secondary" disabled={!title.trim()}>
          Add
        </Button>
      </div>
      {notifyFull && (
        <p id="new-step-notify-full" className="text-sm text-muted-foreground">
          This event has all {notifyMax} of its Notify steps, so new steps are Quiet. Switch a step
          to Quiet to free one up.
        </p>
      )}
    </form>
  )
}

function TriggerDialog({
  d,
  me,
  step,
  onClose,
}: {
  d: EventDetail
  me: MeResponse
  step: StepDto | null
  onClose: () => void
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const refresh = useRefresh(d.event.id)
  const ctx = { appName: me.appName, origin: me.origin, support: me.support }

  useEffect(() => {
    if (step) setNote('')
  }, [step])

  if (!step) return <Dialog open={false} />
  const texts = me.textsEnabled
  const count = recipients(d, step, texts)
  const check = checkStepNote(ctx, d.event.title, step.title, note)
  const preview = templates.step(ctx, {
    prefix: '',
    event: d.event.title,
    step: step.title,
    note: note.trim() || null,
    link: worstLink(ctx).replace(/x+$/, '…'),
  })
  const seconds = Math.round(me.undoWindowMs / 1000)

  async function confirm() {
    if (!step) return
    setBusy(true)
    try {
      await api(`/api/events/${d.event.id}/steps/${step.id}/trigger`, {
        body: { note: note.trim() || null },
      })
      onClose()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
      await refresh()
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark “{step.title}” done?</DialogTitle>
          <DialogDescription>
            {!step.notify
              ? 'Quiet step: it only updates the page. Nobody will be notified.'
              : count === 0
                ? 'Nobody will be notified: no one who signed up gets this step.'
                : `This notifies ${count} ${count === 1 ? 'person' : 'people'} by ${texts ? 'text or email' : 'email'}. You’ll have ${seconds} seconds to undo.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="step-note">Add a short note (optional)</Label>
          <Textarea
            id="step-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="All went well"
            aria-invalid={Boolean(check.problem) || undefined}
          />
          <p
            className={cn('text-sm', check.problem ? 'text-destructive' : 'text-muted-foreground')}
          >
            {check.problem ??
              (texts
                ? `${check.remaining} characters left in one text`
                : `${check.remaining} characters left`)}
          </p>
        </div>
        <div>
          <p className="mb-1.5 text-sm font-medium">{texts ? 'The text' : 'The update'}</p>
          <p className="rounded-2xl rounded-bl-md bg-secondary px-4 py-2.5 text-sm whitespace-pre-wrap">
            {preview}
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void confirm()} disabled={busy || Boolean(check.problem)}>
            <Send /> {count === 0 ? 'Mark done' : 'Send update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ------------------------------------------------------------------ people

const STATUS: Record<ContactDto['status'], { label: string; tone: string }> = {
  invited: { label: 'Not joined yet', tone: 'bg-waiting-soft text-waiting' },
  pending: { label: 'Waiting for approval', tone: 'bg-waiting-soft text-waiting' },
  active: { label: 'Signed up', tone: 'bg-done-soft text-done' },
  denied: { label: 'Turned down', tone: 'bg-muted text-muted-foreground' },
  left: { label: 'Left', tone: 'bg-muted text-muted-foreground' },
  removed: { label: 'Removed', tone: 'bg-muted text-muted-foreground' },
}

function joinUrl(me: MeResponse, code: string): string {
  return `${me.origin}${JOIN_PATH}${code}`
}

async function copyLink(url: string, what: string) {
  try {
    await navigator.clipboard.writeText(url)
    toast.success(`${what} copied`)
  } catch {
    toast.error('Couldn’t copy. Select the link and copy it instead.')
  }
}

function first(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}

/** What happened to the final text, for the toast after turning someone's texts off. */
function finalTextNote(name: string, outcome: FinalText): string {
  switch (outcome) {
    case 'queued':
    case 'sent':
      return `Texts off for ${name}. They get one last text with their link.`
    case 'disabled':
      return `Texts off for ${name}. Texting isn’t active yet, so their last text was recorded but not sent.`
    case 'quota':
      return `Texts off for ${name}. You’ve reached your text limit, so their last text couldn’t go out.`
    case 'none':
      return `Texts off for ${name}. They keep getting email.`
    default:
      return `Texts off for ${name}. Their last text couldn’t go out, but their link still works.`
  }
}

type FinalText =
  | 'none'
  | 'queued'
  | 'sent'
  | 'failed'
  | 'stopped'
  | 'quota'
  | 'invalid'
  | 'no_channel'
  | 'disabled'

/**
 * The Texts and Email switches on one person's row. Texts hold one of the event's SMS seats,
 * so turning someone's texts off frees one (one text per household is plenty). A person with
 * no email left gets one last text with their link, after a confirm; with email, just a toast.
 */
function ChannelSwitches({
  d,
  c,
  onConfirmOff,
  texts,
}: {
  d: EventDetail
  c: ContactDto
  onConfirmOff: (c: ContactDto) => void
  /** Texts-off mode: the Email switch only. */
  texts: boolean
}) {
  const refresh = useRefresh(d.event.id)
  const full = d.usage.smsSeatsUsed >= d.usage.limits.smsSeatsPerEvent
  const canText = texts && Boolean(c.phoneMasked && c.smsConsent)
  const textsBlocked = !c.sms && (full || Boolean(c.optedOut) || c.smsOffBy === 'contact')
  const emailBlocked = !c.email && c.emailOffBy === 'contact'
  const textsHint = c.sms
    ? null
    : c.optedOut
      ? 'Their number opted out (STOP).'
      : c.smsOffBy === 'contact'
        ? 'They turned texts off themselves.'
        : full
          ? 'Free a seat by turning texts off for someone.'
          : null

  const setTexts = (on: boolean) => {
    if (!on && !c.email) return onConfirmOff(c)
    void attempt(async () => {
      const r = await api<{ finalText: FinalText }>(
        `/api/events/${d.event.id}/contacts/${c.id}/channels`,
        { body: { sms: on } },
      )
      toast.success(
        on
          ? `Texts on for ${first(c.name)}. Their next update comes by text.`
          : finalTextNote(first(c.name), r.finalText),
      )
    }, refresh)
  }
  const setEmail = (on: boolean) =>
    void attempt(async () => {
      await api(`/api/events/${d.event.id}/contacts/${c.id}/channels`, { body: { email: on } })
      toast.success(`Email ${on ? 'on' : 'off'} for ${first(c.name)}.`)
    }, refresh)

  if (!canText && !c.emailMasked) return null
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      {canText && (
        <div className="rounded-xl bg-muted px-3 py-2">
          <label
            htmlFor={`texts-${c.id}`}
            className="flex min-h-9 items-center justify-between gap-3 text-sm font-medium"
          >
            <span>
              Texts {c.sms ? 'on' : 'off'}
              <span className="block font-normal text-muted-foreground">{c.phoneMasked}</span>
            </span>
            <Switch
              id={`texts-${c.id}`}
              checked={c.sms}
              disabled={textsBlocked}
              onCheckedChange={setTexts}
              aria-label={`Texts for ${c.name}`}
              data-testid="texts-switch"
            />
          </label>
          {textsHint && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="texts-hint">
              {textsHint}
            </p>
          )}
        </div>
      )}
      {c.emailMasked && (
        <div className="rounded-xl bg-muted px-3 py-2">
          <label
            htmlFor={`email-${c.id}`}
            className="flex min-h-9 items-center justify-between gap-3 text-sm font-medium"
          >
            <span className="min-w-0">
              Email {c.email ? 'on' : 'off'}
              <span className="block truncate font-normal text-muted-foreground">
                {c.emailMasked}
              </span>
            </span>
            <Switch
              id={`email-${c.id}`}
              checked={c.email}
              disabled={emailBlocked}
              onCheckedChange={setEmail}
              aria-label={`Email for ${c.name}`}
              data-testid="email-switch"
            />
          </label>
          {emailBlocked && (
            <p className="mt-1 text-xs text-muted-foreground">They unsubscribed from email.</p>
          )}
        </div>
      )}
    </div>
  )
}

/** How the person gets updates, as the creator sees it. */
function channels(c: ContactDto, texts: boolean): string {
  return [
    texts && c.sms && c.phoneMasked && `Texts ${c.phoneMasked}`,
    c.email && c.emailMasked && `Email ${c.emailMasked}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Requests to join a private event: approve or deny. */
function ApprovalsCard({ d, texts }: { d: EventDetail; texts: boolean }) {
  const refresh = useRefresh(d.event.id)
  const pending = d.contacts.filter((c) => c.status === 'pending')
  if (pending.length === 0) return null
  const act = (c: ContactDto, action: 'approve' | 'deny') =>
    void attempt(async () => {
      await api(`/api/events/${d.event.id}/contacts/${c.id}/${action}`, { method: 'POST' })
      toast.success(
        action === 'approve'
          ? `${c.name.split(' ')[0]} approved. Their confirmation is on its way.`
          : `Request from ${c.name.split(' ')[0]} turned down. Nothing was sent.`,
      )
    }, refresh)
  return (
    <Card className="border-primary/40">
      <SectionTitle aside={`${pending.length} waiting`}>Approvals</SectionTitle>
      <p className="-mt-1 mb-2 text-sm text-muted-foreground">
        Nothing is sent to anyone until you approve them.
      </p>
      <ul className="divide-y">
        {pending.map((c) => (
          <li key={c.id} className="py-3" data-testid={`request-${c.name}`}>
            <p className="font-medium">{c.name}</p>
            <p className="text-sm text-muted-foreground">{channels(c, texts)}</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button onClick={() => act(c, 'approve')}>
                <Check /> Approve
              </Button>
              <Button variant="outline" onClick={() => act(c, 'deny')}>
                Deny
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/** The event's shared join link (copy, replace, turn off) and who can join. */
function JoinLinkCard({ d, me }: { d: EventDetail; me: MeResponse }) {
  const refresh = useRefresh(d.event.id)
  const code = d.event.joinCode
  const url = code ? joinUrl(me, code) : null
  const set = (action: 'rotate' | 'disable', done: string) =>
    void attempt(async () => {
      await api(`/api/events/${d.event.id}/join-link`, { body: { action } })
      toast(done)
    }, refresh)
  const setVisibility = (visibility: 'public' | 'private') =>
    void attempt(
      () => api(`/api/events/${d.event.id}`, { method: 'PATCH', body: { visibility } }),
      refresh,
    )
  return (
    <Card>
      <SectionTitle>Join link</SectionTitle>
      <p className="-mt-1 mb-3 text-sm text-muted-foreground">
        {me.textsEnabled
          ? 'Share this link with anyone who wants updates. They give an email and, if they want texts, a mobile number, and tick a box to agree to texts.'
          : 'Share this link with anyone who wants updates. They sign up with their email address.'}{' '}
        {me.appName} never messages anyone who hasn’t signed up.
      </p>
      <div
        role="radiogroup"
        aria-label="Who can join"
        className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-muted p-1"
      >
        {(
          [
            { value: 'private', label: 'I approve each person' },
            { value: 'public', label: 'Anyone with the link' },
          ] as const
        ).map((o) => (
          // biome-ignore lint/a11y/useSemanticElements: a segmented control with radio semantics
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={d.event.visibility === o.value}
            onClick={() => setVisibility(o.value)}
            className={cn(
              'min-h-11 rounded-lg px-2 text-sm font-medium',
              d.event.visibility === o.value ? 'bg-card shadow-sm' : 'text-muted-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      {url ? (
        <>
          <div className="flex gap-2">
            <Input
              readOnly
              value={url}
              aria-label="Join link"
              onFocus={(e) => e.currentTarget.select()}
              className="font-mono text-sm"
              data-testid="event-join-link"
            />
            <Button onClick={() => void copyLink(url, 'Join link')} className="shrink-0">
              <Copy /> Copy
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => set('rotate', 'New link made. The old one no longer works.')}
            >
              <RefreshCw /> New link
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => set('disable', 'Join link turned off.')}
            >
              <Link2Off /> Turn off
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3">
          <p className="text-sm">
            The join link is off. Personal links for people you’ve added still work.
          </p>
          <Button size="sm" onClick={() => set('rotate', 'Join link turned on.')}>
            <Link2 /> Turn on
          </Button>
        </div>
      )}
    </Card>
  )
}

function PeoplePanel({ d, me }: { d: EventDetail; me: MeResponse }) {
  const refresh = useRefresh(d.event.id)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [removing, setRemoving] = useState<ContactDto | null>(null)
  const [textsOff, setTextsOff] = useState<ContactDto | null>(null)
  const seats = d.usage.limits.smsSeatsPerEvent
  const seatsFull = d.usage.smsSeatsUsed >= seats
  const archived = d.event.status === 'archived'
  const limit = d.usage.limits.contactsPerEvent
  const texts = me.textsEnabled

  async function add(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await api(`/api/events/${d.event.id}/contacts`, {
        body: { name, email, ...(texts && phone.trim() ? { phone } : {}) },
      })
      setName('')
      setEmail('')
      setPhone('')
      toast.success(
        `${name.trim().split(/\s+/)[0]} added. Copy their join link below and send it to them.`,
      )
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? { field: err.field, message: err.message }
          : { message: 'Try again.' },
      )
    } finally {
      await refresh()
    }
  }

  return (
    <div className="space-y-4">
      {!archived && <ApprovalsCard d={d} texts={texts} />}
      {!archived && <JoinLinkCard d={d} me={me} />}
      {!archived && (
        <Card>
          <SectionTitle aside={`${d.contacts.length} of ${limit}`}>Add someone</SectionTitle>
          <form onSubmit={add} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                value={name}
                maxLength={MAX_CONTACT_NAME}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                required
              />
              {error?.field === 'name' && <FieldError>{error.message}</FieldError>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email (required)</Label>
              <Input
                id="contact-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                autoComplete="off"
                required
                aria-invalid={error?.field === 'email' || undefined}
              />
              {error?.field === 'email' && <FieldError>{error.message}</FieldError>}
            </div>
            {texts && (
              <div className="space-y-1.5">
                <Label htmlFor="contact-phone">Mobile number (optional, for texts)</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-0123"
                  autoComplete="off"
                  aria-invalid={error?.field === 'phone' || undefined}
                />
                {error?.field === 'phone' && <FieldError>{error.message}</FieldError>}
              </div>
            )}
            {error && (!error.field || (error.field === 'phone' && !texts)) && (
              <FieldError>{error.message}</FieldError>
            )}
            <p className="rounded-xl bg-muted px-4 py-3 text-sm">
              <strong>We won’t message them.</strong> You’ll get a personal join link to send them
              yourself, however you like. They only get updates after they open it and sign up
              {texts ? ', by email, and by text if they like.' : ' with their email.'}
            </p>
            <Button type="submit" className="w-full" disabled={!name.trim() || !email.trim()}>
              <UserPlus /> Add person
            </Button>
          </form>
        </Card>
      )}

      <Card>
        <SectionTitle
          aside={
            texts && (
              <span className="flex flex-wrap items-center justify-end gap-1.5">
                <span
                  data-testid="sms-seats"
                  className={cn(
                    'rounded-full px-2.5 py-0.5 font-medium',
                    seatsFull ? 'bg-waiting-soft text-waiting' : 'bg-muted text-foreground',
                  )}
                >
                  Texts: {d.usage.smsSeatsUsed} / {seats} seats
                </span>
                <Link
                  to="/account/texts"
                  search={{ event: d.event.id }}
                  data-testid="texts-used"
                  className="rounded-full bg-muted px-2.5 py-0.5 font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Texts used: {d.usage.textsUsed.toLocaleString('en-US')}
                </Link>
              </span>
            )
          }
        >
          People
        </SectionTitle>
        {texts && (
          <p className="-mt-1 mb-2 text-sm text-muted-foreground">
            Tip: one text per household is plenty, since everyone else can follow by email or their
            link.{' '}
            <span className="whitespace-nowrap">
              {d.usage.textsLast30Days} of {d.usage.limits.textsPerCreatorPer30Days} texts used this
              month.
            </span>
          </p>
        )}
        {d.contacts.length === 0 && (
          <p className="text-muted-foreground">No one yet. Share the join link to get started.</p>
        )}
        <ul className="divide-y">
          {d.contacts
            .filter((c) => c.status !== 'pending')
            .map((c) => {
              const personal = c.joinCode && !archived ? joinUrl(me, c.joinCode) : null
              return (
                <li key={c.id} className="py-3" data-testid={`contact-${c.name}`}>
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{c.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[texts && c.phoneMasked, c.emailMasked].filter(Boolean).join(' · ')}
                      </p>
                      <p className="mt-1 flex flex-wrap gap-1.5">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-0.5 text-sm',
                            STATUS[c.status].tone,
                          )}
                        >
                          {STATUS[c.status].label}
                        </span>
                        {(c.status === 'active' || c.status === 'pending') && !c.emailMasked && (
                          <span
                            className="rounded-full bg-waiting-soft px-2.5 py-0.5 text-sm text-waiting"
                            data-testid="no-email"
                            title="Signed up before email was required. Their page asks them to add one."
                          >
                            No email
                          </span>
                        )}
                        {c.status === 'active' && !(texts && c.sms) && !c.email && (
                          <span
                            className="rounded-full bg-muted px-2.5 py-0.5 text-sm text-muted-foreground"
                            data-testid="link-only"
                          >
                            Link only
                          </span>
                        )}
                        {texts && c.optedOut && (
                          <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-sm text-destructive">
                            {c.optedOut === 'stop'
                              ? 'This number has opted out (STOP)'
                              : 'This number has opted out of all events'}
                          </span>
                        )}
                      </p>
                    </div>
                    {!archived && c.status !== 'left' && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${c.name}`}
                        onClick={() => setRemoving(c)}
                      >
                        <Trash2 />
                      </Button>
                    )}
                  </div>
                  {c.status === 'active' && !archived && (
                    <ChannelSwitches d={d} c={c} onConfirmOff={setTextsOff} texts={texts} />
                  )}
                  {personal && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-muted px-3 py-2">
                      <span className="min-w-0 flex-1 text-sm">
                        <span className="block text-muted-foreground">
                          Their personal join link
                        </span>
                        <span
                          className="block font-mono break-all"
                          data-testid="personal-join-link"
                        >
                          {personal}
                        </span>
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Copy ${c.name}’s join link`}
                        onClick={() =>
                          void copyLink(personal, `${c.name.split(' ')[0]}’s join link`)
                        }
                      >
                        <Copy /> Copy link
                      </Button>
                    </div>
                  )}
                </li>
              )
            })}
        </ul>
      </Card>

      <Dialog open={Boolean(textsOff)} onOpenChange={(open) => !open && setTextsOff(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off texts for {textsOff?.name}?</DialogTitle>
            <DialogDescription>
              {textsOff ? first(textsOff.name) : ''} will get one last text with their link. That
              link keeps working, so they can still follow along, or add an email there.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTextsOff(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const c = textsOff
                setTextsOff(null)
                if (c)
                  void attempt(async () => {
                    const r = await api<{ finalText: FinalText }>(
                      `/api/events/${d.event.id}/contacts/${c.id}/channels`,
                      { body: { sms: false } },
                    )
                    toast.success(finalTextNote(first(c.name), r.finalText))
                  }, refresh)
              }}
            >
              Turn off texts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(removing)} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              They stop getting {texts ? 'texts' : 'updates'}, and their links stop working. They
              are not notified.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const c = removing
                setRemoving(null)
                if (c)
                  void attempt(
                    () => api(`/api/events/${d.event.id}/contacts/${c.id}`, { method: 'DELETE' }),
                    refresh,
                  )
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
